import type { ContentfulArticleGroupModel } from 'autogen/swagger/all';
import Layout from 'components/Layout';
import { withErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import ArticleCardList from './ArticleCardList';
import { breakpoint } from 'variables';
import { Spacing24, Spacing8 } from 'autogen/design-tokens/tokens';
import { ButtonWithSpinner } from 'components/Button';
import { useTranslation } from 'react-i18next';
import { ShowMoreArticles } from 'autogen/translation-keys/trans-website-liveevent';
import { useCallback, useContext, useState } from 'react';
import ArticleSearchResponseContext from 'components/pages/ArticleUniverse/components/Filter/context/ArticleSearchResponseContext';

// #region Styles
const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${Spacing8};

    ${breakpoint.up('768px')} {
        grid-template-columns: repeat(3, 1fr);
    }

    ${breakpoint.up('1025px')} {
        grid-template-columns: repeat(4, 1fr);
        gap: ${Spacing24};
    }
`;

const Paging = styled.div`
    justify-self: center;
`;
// #endregion Styles

function ArticleCardGridSection({ articleGroup, ...restProps }: { articleGroup: ContentfulArticleGroupModel }) {
    const { articles } = articleGroup;
    const { hits, paging } = articles;
    const [currentPage, setCurrentPage] = useState(paging.currentPage);
    const [showPaging, setShowPaging] = useState(currentPage < paging.numberOfPages);
    const { t } = useTranslation();
    const articleSearchResponseContext = useContext(ArticleSearchResponseContext);

    const getNextPage = useCallback(async () => {
        const nextPage = currentPage + 1;
        await articleSearchResponseContext?.updateArticlePage({ page: nextPage, pagesize: 24 }, true);
        setCurrentPage(nextPage);
        setShowPaging(nextPage < paging.numberOfPages);
    }, [currentPage, paging.numberOfPages, articleSearchResponseContext]);

    return (
        <>
            <Layout.Section contentRenderer={(children) => <>{children}</>} {...restProps}>
                <Grid>
                    <ArticleCardList articles={hits} />
                </Grid>

                {showPaging && articleSearchResponseContext && (
                    <Paging>
                        <ButtonWithSpinner variant="primaryInverted" onClick={getNextPage}>
                            {t(ShowMoreArticles)}
                        </ButtonWithSpinner>
                    </Paging>
                )}
            </Layout.Section>
        </>
    );
}

export default withErrorBoundary(ArticleCardGridSection, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
